/**
 * EV Charging Sessions Chart
 * Visualizes EV charging session analytics
 */

import { BarChart } from './BarChart';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { useState, useEffect } from 'react';
import { subDays } from 'date-fns';

interface EVSessionsChartProps {
  siteId: string;
}

export function EVSessionsChart({ siteId }: EVSessionsChartProps) {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  const getTimeRange = () => {
    const end = new Date();
    const start = timeRange === '7d'
      ? subDays(end, 7)
      : timeRange === '30d'
      ? subDays(end, 30)
      : subDays(end, 90);

    return { start, end };
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ev-sessions', siteId, timeRange],
    queryFn: async () => {
      const { start, end } = getTimeRange();
      const interval = '1day';

      const response = await api.get(
        `/v1/sites/${siteId}/timeseries?` +
        `asset_type=ev_charging&` +
        `kpis=session_count,energy_delivered&` +
        `start=${start.toISOString()}&` +
        `end=${end.toISOString()}&` +
        `interval=${interval}&` +
        `aggregation=sum`
      );

      const sessionCount = response.data.data.series.session_count || [];
      const energyDelivered = response.data.data.series.energy_delivered || [];

      // Merge data by timestamp
      const merged = sessionCount.map((session: any) => {
        const energy = energyDelivered.find((e: any) => e.timestamp === session.timestamp);
        return {
          timestamp: session.timestamp,
          sessions: session.value,
          energy: energy?.value || 0
        };
      });

      return merged;
    },
    refetchInterval: 60000,
    enabled: !!siteId
  });

  useEffect(() => {
    if (isError) {
      console.error('EVSessionsChart: timeseries query failed', error);
    }
  }, [isError, error]);

  return (
    <div className="rounded-xl border border-slate-700/50 p-6 backdrop-blur-md"
      style={{
        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(51, 65, 85, 0.5) 100%)'
      }}>

      {/* Header with time range selector */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">
          EV Charging Sessions
        </h3>

        <div className="flex gap-2">
          {(['7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 rounded-lg text-sm transition-all ${
                timeRange === range
                  ? 'bg-green-500 text-djin-navy'
                  : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {range.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {isError && (!data || data.length === 0) ? (
        <div
          className="bg-slate-800/30 rounded-lg flex items-center justify-center"
          style={{ height: 300 }}
          role="alert"
        >
          <p className="text-slate-400">Unable to load chart data</p>
        </div>
      ) : (
        <BarChart
          data={data}
          series={[
            {
              name: 'Sessions',
              dataKey: 'sessions',
              color: '#10B981',
              unit: 'count'
            },
            {
              name: 'Energy Delivered',
              dataKey: 'energy',
              color: '#00D4FF',
              unit: 'kWh'
            }
          ]}
          height={300}
          yAxisDomain={['auto', 'auto']}
          loading={isLoading}
          xAxisFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        />
      )}

      {/* Legend */}
      <div className="flex gap-4 mt-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-slate-300">Sessions per day</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-djin-cyan" />
          <span className="text-slate-300">Energy delivered (kWh)</span>
        </div>
      </div>
    </div>
  );
}
